export interface PairingScreenOptions {
  initialCode: string;
  requiresHomeScreen: boolean;
  alreadyPaired?: boolean;
  activate: (code: string) => Promise<void>;
}

export function pairingCodeFromUrl(url: URL): string {
  return url.searchParams.get('code')?.trim() ?? '';
}

export function requiresIosHomeScreen(
  userAgent: string,
  isStandalone: boolean,
): boolean {
  return /iphone|ipad|ipod/i.test(userAgent) && !isStandalone;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export function renderPairingScreen(
  root: HTMLElement,
  options: PairingScreenOptions,
): void {
  root.replaceChildren();
  const card = element('section', 'card');
  const eyebrow = element('p', 'eyebrow');
  eyebrow.textContent = 'Vinculación segura';
  const title = element('h1');
  title.textContent = 'Conecta este celular';
  const description = element('p', 'description');
  description.textContent =
    'Este equipo recibirá únicamente las llamadas asignadas a tu usuario de Bitrix.';
  card.append(eyebrow, title, description);

  if (options.alreadyPaired) {
    const status = element('p', 'status success');
    status.textContent = 'Celular vinculado y listo';
    card.append(status);
    root.append(card);
    return;
  }

  if (options.requiresHomeScreen) {
    const instructions = element('div', 'notice');
    const heading = element('h2');
    heading.textContent = 'Agregar a pantalla de inicio';
    const text = element('p');
    text.textContent =
      'En Safari, toca Compartir y luego “Agregar a inicio”. Abre la app desde el nuevo ícono para continuar.';
    instructions.append(heading, text);
    card.append(instructions);
    root.append(card);
    return;
  }

  const label = element('label');
  label.htmlFor = 'pairing-code';
  label.textContent = 'Código del vendedor';
  const input = element('input');
  input.id = 'pairing-code';
  input.name = 'pairing-code';
  input.autocomplete = 'one-time-code';
  input.value = options.initialCode;
  input.placeholder = 'Pega aquí tu código';
  const button = element('button', 'primary-button');
  button.type = 'button';
  button.textContent = 'Activar notificaciones y vincular';
  const status = element('p', 'status');
  status.setAttribute('role', 'status');
  card.append(label, input, button, status);

  button.addEventListener('click', async () => {
    const code = input.value.trim();
    if (!code) {
      status.textContent = 'Ingresa el código de vinculación.';
      status.className = 'status error';
      return;
    }
    button.disabled = true;
    button.textContent = 'Vinculando…';
    status.textContent = 'Acepta el permiso de notificaciones en tu celular.';
    status.className = 'status';
    try {
      await options.activate(code);
      status.textContent = 'Celular vinculado y listo';
      status.className = 'status success';
      button.textContent = 'Vinculación completada';
    } catch (error) {
      status.textContent =
        error instanceof Error ? error.message : 'No se pudo vincular el celular.';
      status.className = 'status error';
      button.disabled = false;
      button.textContent = 'Intentar nuevamente';
    }
  });

  root.append(card);
}
